import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import subscriptionHistoryService from "../../services/subscriptionHistoryService";
import { getSubscriptionPackages } from "../../services/subscriptionService";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../utils/formModalStyles";

const SubscriptionManagement = () => {
  const { user } = useAuth();
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    packageType: "MONTHLY",
    amount: "",
    paymentMethod: "ONLINE",
    notes: "",
    subscriptionType: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [packageDetails, setPackageDetails] = useState(null);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [loadingPackageDetails, setLoadingPackageDetails] = useState(false);

  useEffect(() => {
    if (user?.organizationId) {
      loadData();
    }
  }, [user?.organizationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [subscription, types] = await Promise.all([
        subscriptionHistoryService.getActiveSubscription(user.organizationId),
        subscriptionHistoryService.getSubscriptionTypes(),
      ]);
      setActiveSubscription(subscription);
      setSubscriptionTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  // Calculate price based on package type (monthly, quarterly, yearly)
  const calculatePrice = (basePrice, pkgType) => {
    switch (pkgType) {
      case "QUARTERLY":
        return basePrice * 3; // 3 months
      case "YEARLY":
        return basePrice * 12; // 12 months
      case "MONTHLY":
      default:
        return basePrice; // Base price is monthly
    }
  };

  // Fetch package details for renewal
  const fetchRenewalPackageDetails = async () => {
    try {
      setLoadingPackageDetails(true);
      const currentTypeName = activeSubscription.subscriptionType?.subType || 
                              activeSubscription.subscriptionType || 
                              "";
      
      if (!currentTypeName) {
        setError("Unable to determine current subscription type");
        return;
      }

      const { packages } = await getSubscriptionPackages();
      const currentPackage = packages.find(
        (pkg) => pkg.name?.toUpperCase() === currentTypeName.toUpperCase() ||
                 pkg.id?.toLowerCase() === currentTypeName.toLowerCase()
      );

      if (currentPackage) {
        setPackageDetails(currentPackage);
        const basePrice = currentPackage.price || 0;
        const calculated = calculatePrice(basePrice, formData.packageType);
        setCalculatedAmount(calculated);
        setFormData(prev => ({ ...prev, amount: calculated.toString() }));
      } else {
        setError("Package details not found");
      }
    } catch (err) {
      console.error("Error fetching package details:", err);
      setError("Failed to fetch package details");
      toast.error("Failed to fetch package details");
    } finally {
      setLoadingPackageDetails(false);
    }
  };

  // Fetch package details for upgrade/downgrade
  const fetchUpgradePackageDetails = async (subscriptionTypeName) => {
    try {
      setLoadingPackageDetails(true);
      if (!subscriptionTypeName) {
        setPackageDetails(null);
        setCalculatedAmount(0);
        setFormData(prev => ({ ...prev, amount: "" }));
        return;
      }

      const { packages } = await getSubscriptionPackages();
      const selectedPackage = packages.find(
        (pkg) => pkg.name?.toUpperCase() === subscriptionTypeName.toUpperCase() ||
                 pkg.id?.toLowerCase() === subscriptionTypeName.toLowerCase()
      );

      if (selectedPackage) {
        setPackageDetails(selectedPackage);
        const basePrice = selectedPackage.price || 0;
        const calculated = calculatePrice(basePrice, formData.packageType);
        setCalculatedAmount(calculated);
        setFormData(prev => ({ ...prev, amount: calculated.toString() }));
      } else {
        setError("Package details not found");
        setPackageDetails(null);
        setCalculatedAmount(0);
        setFormData(prev => ({ ...prev, amount: "" }));
      }
    } catch (err) {
      console.error("Error fetching package details:", err);
      setError("Failed to fetch package details");
      toast.error("Failed to fetch package details");
    } finally {
      setLoadingPackageDetails(false);
    }
  };

  // Handle package type change - recalculate amount
  const handlePackageTypeChange = (newPackageType) => {
    if (packageDetails) {
      const basePrice = packageDetails.price || 0;
      const calculated = calculatePrice(basePrice, newPackageType);
      setCalculatedAmount(calculated);
      setFormData(prev => ({ ...prev, packageType: newPackageType, amount: calculated.toString() }));
    }
  };

  // Handle subscription type change for upgrade/downgrade
  const handleSubscriptionTypeChange = (newSubscriptionType) => {
    setFormData(prev => ({ ...prev, subscriptionType: newSubscriptionType }));
    fetchUpgradePackageDetails(newSubscriptionType);
  };

  const handleRenew = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!calculatedAmount || calculatedAmount <= 0) {
        setError("Invalid amount calculated");
        return;
      }

      await subscriptionHistoryService.renewSubscription({
        organizationId: user.organizationId,
        amount: calculatedAmount,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentMethod === "CASH" ? "COMPLETED" : undefined, // Set to COMPLETED for cash payments
        notes: formData.notes,
      });

      setSuccess("Subscription renewed successfully!");
      toast.success("Subscription renewed successfully!");
      setRenewDialogOpen(false);
      setFormData({
        packageType: "MONTHLY",
        amount: "",
        paymentMethod: "ONLINE",
        notes: "",
      });
      setPackageDetails(null);
      setCalculatedAmount(0);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to renew subscription";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleUpgradeDowngrade = async (actionType) => {
    try {
      setError(null);
      setSuccess(null);

      if (!formData.subscriptionType) {
        setError("Please select a subscription type");
        return;
      }

      if (!calculatedAmount || calculatedAmount <= 0) {
        setError("Invalid amount calculated");
        return;
      }

      await subscriptionHistoryService.changeSubscription({
        organizationId: user.organizationId,
        newSubscriptionType: formData.subscriptionType,
        packageType: formData.packageType,
        amount: calculatedAmount,
        paymentMethod: formData.paymentMethod,
        paymentStatus: formData.paymentMethod === "CASH" ? "COMPLETED" : undefined, // Set to COMPLETED for cash payments
        actionType: actionType,
        notes: formData.notes,
      });

      setSuccess(`Subscription ${actionType.toLowerCase()}d successfully!`);
      toast.success(`Subscription ${actionType.toLowerCase()}d successfully!`);
      setUpgradeDialogOpen(false);
      setFormData({
        packageType: "MONTHLY",
        amount: "",
        paymentMethod: "ONLINE",
        notes: "",
        subscriptionType: "",
      });
      setPackageDetails(null);
      setCalculatedAmount(0);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Failed to ${actionType.toLowerCase()} subscription`;
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!activeSubscription) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No active subscription found. Please contact support.
        </Alert>
      </Box>
    );
  }

  // Handle subscription type - can be object or string
  const currentTypeName = activeSubscription.subscriptionType?.subType || 
                          activeSubscription.subscriptionType || 
                          "";

  const availableUpgrades = subscriptionTypes.filter(
    (type) => {
      const typeName = type.subType || type.name || "";
      return typeName !== currentTypeName &&
        (typeName === "PREMIUM" ||
          (typeName === "INTERMEDIATE" && currentTypeName === "BASIC"));
    }
  );

  const availableDowngrades = subscriptionTypes.filter(
    (type) => {
      const typeName = type.subType || type.name || "";
      return typeName !== currentTypeName &&
        (typeName === "BASIC" ||
          (typeName === "INTERMEDIATE" && currentTypeName === "PREMIUM"));
    }
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Subscription Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Subscription
          </Typography>
          <Typography variant="body1">
            Type: {currentTypeName}
          </Typography>
          <Typography variant="body1">
            Package: {activeSubscription.packageType}
          </Typography>
          <Typography variant="body1">
            End Date: {new Date(activeSubscription.endDate).toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setRenewDialogOpen(true);
              // Reset form data and fetch package details when opening renew dialog
              setFormData({
                packageType: "MONTHLY",
                amount: "",
                paymentMethod: "ONLINE",
                notes: "",
                subscriptionType: "",
              });
              setPackageDetails(null);
              setCalculatedAmount(0);
              setError(null);
              // Fetch package details after a short delay to ensure activeSubscription is available
              setTimeout(() => {
                if (activeSubscription) {
                  fetchRenewalPackageDetails();
                }
              }, 100);
            }}
          >
            Renew Subscription
          </Button>
        </Grid>

        {availableUpgrades.length > 0 && (
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={() => {
                setUpgradeDialogOpen(true);
                // Reset form data when opening upgrade dialog
                setFormData({
                  packageType: "MONTHLY",
                  amount: "",
                  paymentMethod: "ONLINE",
                  notes: "",
                  subscriptionType: "",
                });
                setPackageDetails(null);
                setCalculatedAmount(0);
                setError(null);
              }}
            >
              Upgrade Subscription
            </Button>
          </Grid>
        )}

        {availableDowngrades.length > 0 && (
          <Grid item xs={12} md={4}>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => {
                setUpgradeDialogOpen(true);
                // Reset form data when opening downgrade dialog
                setFormData({
                  packageType: "MONTHLY",
                  amount: "",
                  paymentMethod: "ONLINE",
                  notes: "",
                  subscriptionType: "",
                });
                setPackageDetails(null);
                setCalculatedAmount(0);
                setError(null);
              }}
            >
              Downgrade Subscription
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Renew Dialog */}
      <Dialog open={renewDialogOpen} onClose={() => setRenewDialogOpen(false)} maxWidth="md" fullWidth sx={STANDARD_FORM_DIALOG_PAPER_SX}>
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Renew Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Package Type</InputLabel>
              <Select
                value={formData.packageType}
                onChange={(e) => handlePackageTypeChange(e.target.value)}
                label="Package Type"
                disabled={loadingPackageDetails || !packageDetails}
              >
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                <MenuItem value="YEARLY">Yearly</MenuItem>
              </Select>
            </FormControl>

            {loadingPackageDetails ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography>Loading package details...</Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={calculatedAmount > 0 ? calculatedAmount.toFixed(2) : ""}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
                helperText={packageDetails ? `Base price: ₹${packageDetails.price || 0}/month` : "Select package type to calculate amount"}
              />
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={formData.paymentMethod}
                onChange={(e) =>
                  setFormData({ ...formData, paymentMethod: e.target.value })
                }
                label="Payment Method"
              >
                <MenuItem value="ONLINE">Online</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setRenewDialogOpen(false)} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>Cancel</Button>
          <Button onClick={handleRenew} variant="contained" color="primary" sx={FORM_SUBMIT_BUTTON_SX}>
            Renew
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upgrade/Downgrade Dialog */}
      <Dialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={STANDARD_FORM_DIALOG_PAPER_SX}
      >
        <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>Change Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>New Subscription Type</InputLabel>
              <Select
                value={formData.subscriptionType}
                onChange={(e) => handleSubscriptionTypeChange(e.target.value)}
                label="New Subscription Type"
                disabled={loadingPackageDetails}
              >
                <MenuItem value="">
                  <em>Select subscription type</em>
                </MenuItem>
                {[...availableUpgrades, ...availableDowngrades].map((type) => {
                  const typeName = type.subType || type.name || "";
                  const price = type.packageAmount || type.price || 0;
                  return (
                    <MenuItem key={type.id || typeName} value={typeName}>
                      {typeName} - ₹{price}/month
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Package Type</InputLabel>
              <Select
                value={formData.packageType}
                onChange={(e) => handlePackageTypeChange(e.target.value)}
                label="Package Type"
                disabled={loadingPackageDetails || !packageDetails}
              >
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                <MenuItem value="YEARLY">Yearly</MenuItem>
              </Select>
            </FormControl>

            {loadingPackageDetails ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography>Loading package details...</Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={calculatedAmount > 0 ? calculatedAmount.toFixed(2) : ""}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
                helperText={packageDetails ? `Base price: ₹${packageDetails.price || 0}/month` : "Select subscription type to calculate amount"}
              />
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={formData.paymentMethod}
                onChange={(e) =>
                  setFormData({ ...formData, paymentMethod: e.target.value })
                }
                label="Payment Method"
              >
                <MenuItem value="ONLINE">Online</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
          <Button onClick={() => setUpgradeDialogOpen(false)} variant="outlined" color="primary" sx={FORM_CANCEL_BUTTON_SX}>Cancel</Button>
          {availableUpgrades.some(
            (t) => (t.subType || t.name) === formData.subscriptionType
          ) && (
            <Button
              onClick={() => handleUpgradeDowngrade("UPGRADE")}
              variant="contained"
              color="primary"
              sx={FORM_SUBMIT_BUTTON_SX}
            >
              Upgrade
            </Button>
          )}
          {availableDowngrades.some(
            (t) => (t.subType || t.name) === formData.subscriptionType
          ) && (
            <Button
              onClick={() => handleUpgradeDowngrade("DOWNGRADE")}
              variant="contained"
              color="primary"
              sx={FORM_SUBMIT_BUTTON_SX}
            >
              Downgrade
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagement;

